/**
 * DeckShareView — the private, self-navigated deck viewer.
 *
 * Patrick 2026-08-14: "I just share the pitch deck now with someone to watch the
 * static version of it instead of creating a PDF file… a private link with a long
 * private code behind… easy controls, left, right, up, down, page down, page up,
 * should easily control or with a mouse, maybe scroll up, scroll down."
 *
 * 🔴 THIS RENDERS OUTSIDE THE ADMIN LOGIN GATE (App.jsx). It is reached only with
 * the deck's 256-bit view_token and shows only what the public endpoint returns:
 * published slides of ONE deck. No transcript, no editor, no other deck, no
 * speaker notes, no reserve slides.
 *
 * 🔴 IT IS NOT THE LIVE MIRROR. LivePitchView asks the server which slide to show
 * every 800ms and follows Patrick. This one holds its own position and never
 * polls — the reader drives it. That is the whole point: a link sent last week
 * must still show the deck it was made from, not whatever is being rehearsed now.
 * They also read different endpoints and different tokens, so neither can drag
 * the other around.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SlideCanvas from './SlideCanvas';

const API = (process.env.REACT_APP_BACKEND_URL || 'https://core.honestlee.app').replace(/\/$/, '');

export default function DeckShareView() {
  // /deck/<token> — the code is the path, so the link survives being pasted into
  // apps that mangle query strings.
  const token = (window.location.pathname.split('/').filter(Boolean)[1] || '').trim();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [i, setI] = useState(0);
  const [size, setSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [showUi, setShowUi] = useState(true);
  const hideTimer = useRef(null);
  const wheelLock = useRef(0);
  const touchY = useRef(null);
  const touchX = useRef(null);

  useEffect(() => {
    if (!token || token.length < 32) { setErr('This link is missing its code.'); return; }
    let alive = true;
    fetch(`${API}/api/pitch/deck/${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(j => {
        if (!alive) return;
        if (j.success === false) { setErr('This link is not active.'); return; }
        if (!j.slides?.length) { setErr('This deck has no published slides yet.'); return; }
        setData(j);
      })
      .catch(() => alive && setErr('Could not load the deck.'));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const total = data?.slides?.length || 0;
  const go = useCallback((d) => {
    setI(prev => Math.min(total - 1, Math.max(0, prev + d)));
    setShowUi(true);
  }, [total]);
  const jump = useCallback((n) => { setI(Math.min(total - 1, Math.max(0, n))); setShowUi(true); }, [total]);

  // The chrome fades out while reading and comes back on any input, so a shared
  // deck looks like a deck rather than an app.
  useEffect(() => {
    if (!showUi) return;
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowUi(false), 2600);
    return () => clearTimeout(hideTimer.current);
  }, [showUi, i]);

  // ── KEYBOARD ────────────────────────────────────────────────────────────────
  // Everything Patrick asked for by name, plus the keys people reach for without
  // thinking: space, backspace, Home/End, and the presenter-remote keys (which
  // most clickers send as PageUp/PageDown, so they work here for free).
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      let handled = true;
      switch (e.key) {
        case 'ArrowRight': case 'ArrowDown': case 'PageDown': case ' ': case 'Enter':
          go(1); break;
        case 'ArrowLeft': case 'ArrowUp': case 'PageUp': case 'Backspace':
          go(-1); break;
        case 'Home': jump(0); break;
        case 'End': jump(total - 1); break;
        case 'f': case 'F':
          if (document.fullscreenElement) document.exitFullscreen?.();
          else document.documentElement.requestFullscreen?.();
          break;
        case 'Escape': setShowUi(true); handled = false; break;
        default: handled = false;
      }
      if (handled) e.preventDefault();
      else setShowUi(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, jump, total]);

  // ── MOUSE WHEEL / TRACKPAD ──────────────────────────────────────────────────
  // 🔴 A trackpad emits dozens of wheel events per flick. Without the 380ms lock
  // one two-finger swipe would skip five slides, which feels broken rather than
  // fast. Small deltas are ignored so an unintentional nudge does nothing.
  useEffect(() => {
    const onWheel = (e) => {
      const now = Date.now();
      if (now - wheelLock.current < 380) return;
      if (Math.abs(e.deltaY) < 12) return;
      wheelLock.current = now;
      go(e.deltaY > 0 ? 1 : -1);
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => window.removeEventListener('wheel', onWheel);
  }, [go]);

  // ── TOUCH ───────────────────────────────────────────────────────────────────
  // Vertical OR horizontal swipe — on a phone people try both.
  useEffect(() => {
    const start = (e) => { touchY.current = e.touches[0].clientY; touchX.current = e.touches[0].clientX; };
    const end = (e) => {
      if (touchY.current == null) return;
      const dy = e.changedTouches[0].clientY - touchY.current;
      const dx = e.changedTouches[0].clientX - touchX.current;
      const d = Math.abs(dx) > Math.abs(dy) ? dx : dy;
      if (Math.abs(d) > 45) go(d < 0 ? 1 : -1);
      touchY.current = null; touchX.current = null;
    };
    window.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchend', end, { passive: true });
    return () => { window.removeEventListener('touchstart', start); window.removeEventListener('touchend', end); };
  }, [go]);

  const wrap = {
    position: 'fixed', inset: 0, background: '#05070d',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', cursor: showUi ? 'default' : 'none',
  };

  if (err) {
    return (
      <div style={{ ...wrap, color: '#94a3b8', flexDirection: 'column', gap: 10, fontSize: 15 }}>
        <div style={{ fontSize: 34 }}>{'\u{1F512}'}</div>
        <div>{err}</div>
      </div>
    );
  }
  if (!data) return <div style={{ ...wrap, color: '#475569' }}>Loading…</div>;

  const slide = data.slides[i];
  // 🔴 SlideCanvas takes a RESOLVED theme object and a width — not the themes
  // array, and not a height. Getting this contract wrong is exactly how the live
  // screen once rendered every style at its default while the editor looked
  // right: same component, different props. Copied verbatim from LivePitchView.
  const theme = data.themes?.find(t => t.key === (slide?.theme_key || data.deck?.theme_key))
    || data.themes?.[0] || null;
  const w = Math.min(size.w, (size.h * 16) / 9);
  const h = (w * 9) / 16;

  const arrow = (side) => ({
    position: 'absolute', top: 0, bottom: 0, [side]: 0, width: '22%',
    display: 'flex', alignItems: 'center', justifyContent: side === 'left' ? 'flex-start' : 'flex-end',
    padding: '0 14px', border: 0, background: 'transparent',
    color: showUi ? 'rgba(255,255,255,0.34)' : 'transparent',
    fontSize: 34, cursor: 'pointer', transition: 'color .25s', zIndex: 5,
  });

  return (
    <div style={wrap} onMouseMove={() => setShowUi(true)}>
      <div style={{ width: w, height: h, position: 'relative' }}>
        <SlideCanvas slide={slide} theme={theme} deck={data.deck} width={w} />
      </div>

      {/* Click zones — the way most people page a deck without reading instructions */}
      <button aria-label="Previous slide" onClick={() => go(-1)} style={arrow('left')} disabled={i === 0}>‹</button>
      <button aria-label="Next slide" onClick={() => go(1)} style={arrow('right')} disabled={i >= total - 1}>›</button>

      {/* Position + a one-line hint. Fades with the rest of the chrome. */}
      <div style={{
        position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center',
        color: 'rgba(255,255,255,0.5)', fontSize: 12, letterSpacing: 0.3,
        opacity: showUi ? 1 : 0, transition: 'opacity .4s', pointerEvents: 'none',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.78)' }}>{i + 1}</span>
        <span> / {total}</span>
        <span style={{ margin: '0 10px', opacity: 0.35 }}>·</span>
        <span style={{ opacity: 0.75 }}>arrows, space or scroll to move · F for fullscreen</span>
      </div>

      {/* Progress rail — cheap orientation on a long deck, and clickable to jump. */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        display: 'flex', gap: 2, padding: '0 2px',
        opacity: showUi ? 1 : 0, transition: 'opacity .4s', zIndex: 6,
      }}>
        {data.slides.map((s, n) => (
          <button
            key={s.id}
            aria-label={`Go to slide ${n + 1}`}
            onClick={() => jump(n)}
            style={{
              flex: 1, height: 3, border: 0, padding: 0, cursor: 'pointer',
              background: n <= i ? '#E46E34' : 'rgba(255,255,255,0.16)',
              transition: 'background .2s',
            }}
          />
        ))}
      </div>
    </div>
  );
}
